// ============================================================================
// AppointmentCard — карточка одной записи (общая для пациента и врача).
//
// Показывает время, пациента/специалиста, статус, источник, комментарий и
// напоминания. Действия передаются СВЕРХУ (права — SSoT сервера): карточка
// ничего не решает о доступе сама.
// ============================================================================

import { CalendarClock, Check, Clock3, MessageSquare, Stethoscope, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AppointmentStatusBadge } from '@/components/medic/appointments/AppointmentStatusBadge';
import {
  valueDate,
  valueDateKey,
  valueDateTimeLabel,
  isActiveStatus,
  type AppointmentItem,
} from '@/lib/medicAppointments';

export interface AppointmentCardProps {
  item: AppointmentItem;
  statusLabels: Record<string, string>;
  typeLabels: Record<string, string>;
  mode: 'patient' | 'doctor';
  canReschedule: boolean;
  canCancel: boolean;
  canConfirm: boolean;
  canComplete: boolean;
  busy: boolean;
  serverReminder?: string;
  onReschedule: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  onComplete: () => void;
}

function dayHint(value: string): string {
  const date = valueDate(value);
  if (!date) return '';
  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  const diff = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  if (diff === 0) return 'сегодня';
  if (diff === 1) return 'завтра';
  if (diff === -1) return 'вчера';
  return '';
}

function timeRange(item: AppointmentItem): string {
  const start = valueDateTimeLabel(item.startAt);
  if (!item.durationMin) return start;
  return `${start} · ${item.durationMin} мин`;
}

export function AppointmentCard({
  item, statusLabels, typeLabels, mode, canReschedule, canCancel, canConfirm, canComplete,
  busy, serverReminder, onReschedule, onCancel, onConfirm, onComplete,
}: AppointmentCardProps) {
  const active = isActiveStatus(item.source, item.statusCode);
  const hint = dayHint(item.startAt);
  const sourceLabel = item.source === 'core' ? 'Медицинский приём' : 'Запись к специалисту';
  const typeLabel = item.typeLabel || typeLabels[item.typeCode] || '';

  return (
    <article className="rounded-xl border bg-card p-3 shadow-sm">
      <div className="flex flex-wrap items-start gap-2">
        <div className="flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-lg bg-primary/10 text-primary">
          <span className="text-sm font-semibold leading-none">{valueDateKey(item.startAt).slice(8, 10) || '—'}</span>
          <span className="mt-0.5 text-[9px] uppercase leading-none">
            {valueDateKey(item.startAt).slice(5, 7)}.{valueDateKey(item.startAt).slice(0, 4)}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <CalendarClock className="h-3.5 w-3.5 shrink-0 text-primary" />
            <span className="text-sm font-semibold">{timeRange(item)}</span>
            {hint && <span className="rounded-full border bg-muted/40 px-1.5 py-0.5 text-[10px] text-muted-foreground">{hint}</span>}
            <AppointmentStatusBadge item={item} labels={statusLabels} />
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              {mode === 'doctor' ? <UserRound className="h-3 w-3" /> : <Stethoscope className="h-3 w-3" />}
              <span className="text-foreground">
                {mode === 'doctor'
                  ? (item.patientName || 'Пациент не указан')
                  : (item.doctorName || 'Специалист не указан')}
              </span>
            </span>
            <span>{sourceLabel}</span>
            {typeLabel && <span>· {typeLabel}</span>}
            {item.number && <span>· № {item.number}</span>}
          </div>
          {item.comment && (
            <div className="mt-1.5 flex items-start gap-1.5 rounded-lg border bg-muted/20 px-2 py-1.5 text-[11px]">
              <MessageSquare className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="whitespace-pre-wrap break-words">{item.comment}</span>
            </div>
          )}
          {serverReminder && (
            <div className="mt-1.5 flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <Clock3 className="h-3 w-3" />
              Напоминание специалисту: {serverReminder}
            </div>
          )}
        </div>
      </div>

      {(canReschedule || canCancel || canConfirm || canComplete) && (
        <div className="mt-2 flex flex-wrap items-center justify-end gap-1.5 border-t pt-2">
          {canConfirm && active && (
            <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" disabled={busy} onClick={onConfirm}>
              <Check className="mr-1 h-3 w-3" />
              Подтвердить
            </Button>
          )}
          {canComplete && active && (
            <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" disabled={busy} onClick={onComplete}>
              <Check className="mr-1 h-3 w-3" />
              Завершить
            </Button>
          )}
          {canReschedule && (
            <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" disabled={busy} onClick={onReschedule}>
              Перенести
            </Button>
          )}
          {canCancel && (
            <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] text-destructive hover:text-destructive" disabled={busy} onClick={onCancel}>
              Отменить
            </Button>
          )}
        </div>
      )}
    </article>
  );
}
